import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, CheckCheck, Loader2, ShieldAlert, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const TIPO_ICONS = {
  'Estado': ShieldAlert,
  'Candidatura': FileText,
  'Aprovacao': CheckCircle2
};

export default function PortalNotificacoesPage() {
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marcandoTodas, setMarcandoTodas] = useState(false);

  useEffect(() => {
    loadNotificacoes();
  }, []);

  const loadNotificacoes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/notificacoes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setNotificacoes(await response.json());
      }
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const marcarLida = async (id) => {
    try {
      const token = localStorage.getItem('portal_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/notificacoes/${id}/marcar-lida`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    } catch (error) {
      toast.error('Erro ao marcar como lida');
    }
  };

  const marcarTodasLidas = async () => {
    setMarcandoTodas(true);
    try {
      const token = localStorage.getItem('portal_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/notificacoes/marcar-todas-lidas`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
      toast.success('Todas as notificações foram marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações');
    } finally {
      setMarcandoTodas(false);
    }
  };

  const naoLidas = notificacoes.filter(n => !n.lida).length;

  return (
    <div className="space-y-6" data-testid="portal-notificacoes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-[#0066CC]" />
            Notificações
          </h1>
          <p className="text-slate-500 text-sm">Atualizações sobre a sua instituição, candidatura e pedidos</p>
        </div>
        {naoLidas > 0 && (
          <Button variant="outline" onClick={marcarTodasLidas} disabled={marcandoTodas} data-testid="marcar-todas-lidas-btn">
            {marcandoTodas ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : notificacoes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500" data-testid="sem-notificacoes">
            <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
            Ainda não tem notificações
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notificacoes.map((notif) => {
            const Icon = TIPO_ICONS[notif.tipo] || Bell;
            return (
              <Card
                key={notif.id}
                className={`transition-colors ${!notif.lida ? 'border-l-4 border-l-[#0066CC] bg-blue-50/40' : ''}`}
                data-testid={`notificacao-item-${notif.id}`}
              >
                <CardContent className="py-4 flex items-start gap-3">
                  <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${!notif.lida ? 'text-[#0066CC]' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{notif.titulo}</p>
                      {!notif.lida && <Badge className="bg-[#0066CC] text-xs">Nova</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{notif.mensagem}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {notif.created_at ? new Date(notif.created_at).toLocaleString('pt-PT') : ''}
                    </p>
                  </div>
                  {!notif.lida && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => marcarLida(notif.id)}
                      data-testid={`marcar-lida-btn-${notif.id}`}
                    >
                      Marcar lida
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
